import React from 'react';

import { ApiBookedItem, ApiBooking, ApiHotel, ApiItemType } from '@ibe/api';
import dayjs, { PluginFunc } from 'dayjs';
import * as dur from 'dayjs/plugin/duration';
import * as iUTC from 'dayjs/plugin/utc';
import { useAppService, useBookingService, useConfig, useTranslation } from '@ibe/components';

import fallback from '../../../../Translations/generated/price-overview-header.de.json';
import Keys from '../../../../Translations/generated/price-overview-header.de.json.keys';

import { observer } from 'mobx-react';
import {
  SelectedItemsType,
  useExtendedBookingForPriceOverview
} from '../ExtendedBooking/ContextAndProvider';
import { packageIdSessionStorage } from '../../../Util/utils';
import { sanitize } from '@ibe/services';

export const TOUR_OPERATOR_TTAG = 'TTAG';
export const DEFAULT_TOUR_OPERATOR = 'TT';

dayjs.extend((iUTC as unknown) as PluginFunc<unknown>);
dayjs.extend((dur as unknown) as PluginFunc<unknown>);

interface Props {
  booking: ApiBooking;
  title?: string;
  withHotel?: boolean;
  withDuration?: boolean;
  withTravelDates?: boolean;
  withGroupSize?: boolean;
  busStationRow?: string | null;
}

const PriceOverviewHeader = observer(
  ({
    booking,
    title,
    withHotel = false,
    withDuration = true,
    withTravelDates = true,
    withGroupSize = true,
    busStationRow = null
  }: Props) => {
    //--------fetch Items-------------------
    // - selectedItems
    const selectedItems = useExtendedBookingForPriceOverview();

    const travelers = booking?.travelers;
    const numberOfTravelers = travelers?.length || 0;
    const config = useConfig();
    const appService = useAppService();

    const hotel = booking?.items.find(it => it.itemType === ApiItemType.HOTEL) as ApiHotel;
    const {
      bookedPackage,
      formattedEndDate,
      duration,
      formattedStartDate
    } = extendedDateSingleSourceOfTruth(
      config.displayFormatDate[appService.lang.replace('-', '_')],
      booking,
      selectedItems
    );
    // internationalization of duration
    const { t } = useTranslation('price-overview-header', fallback);
    const numberOfDaysLabel = t(Keys.day, {
      count: duration?.total,
      numberOfDay: duration?.total
    });

    const dateToShow = formattedStartDate + ' - ' + formattedEndDate;

    const bs = useBookingService();
    const selectablePackageOffers = !!bs.selectablePackageOffers
      ? [...bs.selectablePackageOffers]
      : [];

    const seasonContent = bookedPackage?.metaInfo?.contentByMetaType?.season?.content as
      | { tourOperator: string }
      | undefined;

    const tourOperator = seasonContent?.tourOperator ?? DEFAULT_TOUR_OPERATOR;

    return (
      <section
        className="iso__price__overview__header"
        role="region"
        aria-labelledby="price-overview-heading"
      >
        <h4 id="price-overview-heading">{title ?? t(Keys.priceOverview)}</h4>
        {withTravelDates ? (
          <div>
            <span>{t(Keys.travelDate)} </span>
            <span> {dateToShow}</span>
          </div>
        ) : (
          <></>
        )}
        <div>
          <span>{t(Keys.numberParticipants)}</span>
          <span>{numberOfTravelers}</span>
        </div>
        {withDuration ? (
          <div>
            <span>{t(Keys.duration)}</span>
            <span>{numberOfDaysLabel}</span>
          </div>
        ) : (
          <></>
        )}
        {withGroupSize ? (
          <div>
            <span>{t(Keys.groupSize)}</span>
            <span>{t(packageIdSessionStorage?.get()?.groupSize ?? '')}</span>
          </div>
        ) : (
          <></>
        )}
        {busStationRow && (
          <div>
            <span>{t(Keys.yourBusStop)}</span>
            <span>{busStationRow}</span>
          </div>
        )}
        {withHotel ? (
          <div>
            <span>{t(Keys.hotel)}</span>
            <span>{bookedPackage.description}</span>
          </div>
        ) : (
          <></>
        )}
        {withHotel && selectablePackageOffers.length > 1 ? (
          <div>
            <span>{t(Keys.hotel)}</span>
            <span>{hotel.name}</span>
          </div>
        ) : (
          <></>
        )}
        {tourOperator && (
          <div>
            <span>{t(Keys.tourOperator)}</span>
            <span
              dangerouslySetInnerHTML={{
                __html: sanitize(
                  t(
                    tourOperator === TOUR_OPERATOR_TTAG
                      ? Keys.tourOperatorTTAG
                      : Keys.tourOperatorTT
                  ),
                  true
                )
              }}
            />
          </div>
        )}
      </section>
    );
  }
);

export const extendedDateSingleSourceOfTruth = (
  dateFormat: string,
  booking: ApiBooking | null,
  selectedItems?: SelectedItemsType | null
) => {
  const { duration } = selectedItems || {};
  const { extensionDuration, mainComponentDuration } = duration || {};

  const bookedPackage = booking?.bookedItems.find(
    it => it.itemType === ApiItemType.PACKAGE
  ) as ApiBookedItem;
  if (mainComponentDuration) {
    const { startDate, endDate } = mainComponentDuration;
    const mainDuration = dayjs(endDate).diff(dayjs(startDate), 'days') + 1;
    return {
      formattedStartDate: dayjs.utc(startDate).format(dateFormat),
      formattedEndDate: dayjs
        .utc(startDate)
        .add(mainDuration - 1 + (extensionDuration ?? 0), 'days')
        .format(dateFormat),
      duration: {
        main: mainDuration,
        extension: extensionDuration,
        total: mainDuration + (extensionDuration ?? 0)
      },
      bookedPackage
    };
  }

  return {
    formattedStartDate: dayjs.utc(bookedPackage?.startDate).format(dateFormat),
    formattedEndDate: dayjs.utc(bookedPackage?.endDate).format(dateFormat),
    duration: {
      total: dayjs(bookedPackage?.endDate).diff(dayjs(bookedPackage?.startDate), 'days') + 1
    },
    bookedPackage
  };
};

export default PriceOverviewHeader;
